import { useState, useEffect, useCallback, useRef } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { ResponsiveGridLayout, useContainerWidth } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import Icon from '@mui/material/Icon';
import { useAuthz } from "context/AuthzContext";
import { auth } from "../../firebase";
import { loadLayout, saveLayout } from "services/dashboardLayoutService";
import { WIDGET_REGISTRY, DEFAULT_WIDGETS, DEFAULT_LAYOUT } from "components/DashboardWidgets/widgetRegistry";
import WidgetFrame from "components/DashboardWidgets/WidgetFrame";
import AddWidgetDialog from "components/DashboardWidgets/AddWidgetDialog";
import ClientBreakdownWidget from "components/DashboardWidgets/widgets/ClientBreakdownWidget";
import AssistantPane from "components/AssistantPane";
import ClientStatsWidget from "components/DashboardWidgets/widgets/ClientStatsWidget";
import UpcomingJobsWidget from "components/DashboardWidgets/widgets/UpcomingJobsWidget";
import QuickActionsWidget from "components/DashboardWidgets/widgets/QuickActionsWidget";
import RecentClientsWidget from "components/DashboardWidgets/widgets/RecentClientsWidget";
// New widgets
import BankBalancesWidget from "components/DashboardWidgets/widgets/BankBalancesWidget";
import AgedReceivablesWidget from "components/DashboardWidgets/widgets/AgedReceivablesWidget";
import VATDeadlinesWidget from "components/DashboardWidgets/widgets/VATDeadlinesWidget";
import CashflowForecastWidget from "components/DashboardWidgets/widgets/CashflowForecastWidget";
import ClientHealthWidget from "components/DashboardWidgets/widgets/ClientHealthWidget";
import RevenueTrendWidget from "components/DashboardWidgets/widgets/RevenueTrendWidget";
import SystemHealthWidget from "components/DashboardWidgets/widgets/SystemHealthWidget";
import { TC } from "../../tokens/text";
import { useTheme } from '@mui/material/styles';


const GRID_PLACEHOLDER_CSS = `
  .react-grid-item.react-grid-placeholder {
    background: rgba(0,117,255,0.2) !important;
    border-radius: 12px;
  }
  .react-grid-item > .react-resizable-handle::after {
    border-color: rgba(255,255,255,0.3) !important;
  }
  @keyframes greetFadeUp {
    from { opacity: 0; transform: translateY(18px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes greetSubFade {
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes waveHand {
    0%   { transform: rotate(0deg); }
    15%  { transform: rotate(-22deg); }
    30%  { transform: rotate(16deg); }
    45%  { transform: rotate(-12deg); }
    60%  { transform: rotate(8deg); }
    75%  { transform: rotate(-4deg); }
    100% { transform: rotate(0deg); }
  }
`;

export default function MainDashboard() {
  const { tenantId, isSuperAdmin, isTenantAdmin } = useAuthz();
  const demoMode = typeof window !== 'undefined' && (window.__LEDGERHUB_DEMO || new URLSearchParams(window.location.search).get('demo') === 'true');
  const theme = useTheme();
  // Keep white text in dark mode (previous behaviour), otherwise use text.primary for light mode
  const forcedContrast = theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary;

  // Track uid as state so load re-runs when Firebase auth resolves after page refresh
  const [uid, setUid] = useState(() => auth.currentUser?.uid ?? null);
  useEffect(() => onAuthStateChanged(auth, (user) => setUid(user?.uid ?? null)), []);

  const effectiveTenantId = tenantId ?? (demoMode ? 'demo-tenant' : null);

  const currentUser = auth.currentUser;

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  })();

  const displayName = (() => {
    if (!currentUser) return "";
    if (currentUser.displayName) return currentUser.displayName.split(" ")[0];
    if (currentUser.email) return currentUser.email.split("@")[0];
    return "";
  })();

  const [widgets, setWidgets] = useState(DEFAULT_WIDGETS);
  const [layouts, setLayouts] = useState({ lg: DEFAULT_LAYOUT });
  const [isEditing, setIsEditing] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [layoutLoaded, setLayoutLoaded] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const { containerRef: gridContainerRef, width: gridWidth } = useContainerWidth({ initialWidth: 1200 });

  // If demo-mode is enabled, show the default starter widgets immediately
  useEffect(() => {
    if (!demoMode) return;
    // Ensure demo default layout uses full-width widgets so users see content across entire dashboard
    const colsLg = gridWidth >= 1200 ? 12 : gridWidth >= 996 ? 10 : gridWidth >= 768 ? 6 : 6;
    const colsMd = 10;
    const colsSm = 6;
    let y = 0;
    const fullLayoutLg = DEFAULT_WIDGETS.map((w) => {
      const reg = WIDGET_REGISTRY[w.type] || {};
      const h = reg.defaultH || 4;
      const item = { i: w.id, x: 0, y: y, w: colsLg, h, minW: reg.minW, minH: reg.minH };
      y += h;
      return item;
    });
    // Build matching md/sm layouts with full width items
    const fullLayoutMd = fullLayoutLg.map((it) => ({ ...it, w: Math.min(it.w, colsMd) }));
    const fullLayoutSm = fullLayoutLg.map((it) => ({ ...it, w: Math.min(it.w, colsSm) }));
    setWidgets(DEFAULT_WIDGETS);
    setLayouts({ lg: fullLayoutLg, md: fullLayoutMd, sm: fullLayoutSm });
    setLayoutLoaded(true);
  }, [demoMode, gridWidth]);

  // Tracks how many times onLayoutChange has fired since last grid mount
  // (used to skip the automatic fire on initial mount)
  const layoutChangeCountRef = useRef(0);

  useEffect(() => {
    console.log("[Dashboard] loadLayout effect", { uid, tenantId });
    if (!uid || !tenantId) { setLayoutLoaded(false); return; }
    setLayoutLoaded(false);
    layoutChangeCountRef.current = 0; // reset so the mount-fire is skipped again after reload
    loadLayout(uid, tenantId)
      .then((saved) => {
        console.log("[Dashboard] loaded layout", saved);
        // `saved` is non-null when localStorage has an entry (even with empty widgets).
        if (saved) {
          // Filter out widget types that have been removed from the registry
          const REMOVED = ["all_jobs", "client_stats", "client_breakdown"];
          const filteredWidgets = (saved.widgets || []).filter(w => !REMOVED.includes(w.type));
          const filteredLayout  = (saved.layout || []).filter(l =>
            !REMOVED.some(t => l.i.startsWith(`w-${t}`))
          );
            // Ensure we expose responsive layouts to the grid so items can span full width per breakpoint
            const colsMd = 10;
            const colsSm = 6;
            const layoutMd = (filteredLayout || []).map((it) => ({ ...it, w: Math.min(it.w || colsMd, colsMd) }));
            const layoutSm = (filteredLayout || []).map((it) => ({ ...it, w: Math.min(it.w || colsSm, colsSm) }));
            setWidgets(filteredWidgets);
            setLayouts({ lg: filteredLayout, md: layoutMd, sm: layoutSm });
          } else {
            // No saved layout: fall back to DEFAULT_LAYOUT but render full-width for clarity
            const colsLg = gridWidth >= 1200 ? 12 : gridWidth >= 996 ? 10 : gridWidth >= 768 ? 6 : 6;
            const colsMd = 10;
            const colsSm = 6;
            let y = 0;
            const fullLayoutLg = DEFAULT_WIDGETS.map((w) => {
              const reg = WIDGET_REGISTRY[w.type] || {};
              const h = reg.defaultH || 4;
              const item = { i: w.id, x: 0, y: y, w: colsLg, h, minW: reg.minW, minH: reg.minH };
              y += h;
              return item;
            });
            const fullLayoutMd = fullLayoutLg.map((it) => ({ ...it, w: Math.min(it.w, colsMd) }));
            const fullLayoutSm = fullLayoutLg.map((it) => ({ ...it, w: Math.min(it.w, colsSm) }));
            setWidgets(DEFAULT_WIDGETS);
            setLayouts({ lg: fullLayoutLg, md: fullLayoutMd, sm: fullLayoutSm });
          }
        setLayoutLoaded(true);
      })
      .catch((e) => { console.error("[Dashboard] loadLayout error", e); setLayoutLoaded(true); });
  }, [uid, tenantId]);

  // Keep a live ref to widgets so drag/resize callbacks always see latest value
  const widgetsRef = useRef(widgets);
  useEffect(() => { widgetsRef.current = widgets; }, [widgets]);

  // Read uid at call time so we always have the latest auth state (not a stale closure)
  const doSave = useCallback((layout, widgetList) => {
    const currentUid = auth.currentUser?.uid;
    console.log("[Dashboard] doSave called", { currentUid, tenantId, layoutLen: layout?.length });
    if (!currentUid || !tenantId || layout == null) {
      console.warn("[Dashboard] doSave skipped — missing uid/tenantId/layout");
      return;
    }
    saveLayout(currentUid, tenantId, layout, widgetList ?? widgetsRef.current)
      .then(() => console.log("[Dashboard] saveLayout OK"))
      .catch((e) => console.error("[Dashboard] saveLayout FAILED", e));
  }, [tenantId]);

  // onLayoutChange keeps the controlled layouts prop in sync with RGL's internal state.
  // Without this, RGL v2.x snaps back to the last `layouts` prop value after every drag.
  // We do NOT save here — only onDragStop/onResizeStop persist to storage.
  // RGL fires onLayoutChange on mount (1-3 times for breakpoints) — skip those.
  const handleLayoutChange = useCallback((currentLayout, allLayouts) => {
    layoutChangeCountRef.current += 1;
    if (layoutChangeCountRef.current <= 3) return;
    // Filter to only items that still exist in the current widget list.
    // Without this, RGL can fire with stale allLayouts after a widget removal
    // and restore the removed item back into the layouts state.
    const validIds = new Set(widgetsRef.current.map((w) => w.id));
    const filteredLayouts = {
      ...allLayouts,
      lg: (allLayouts.lg || []).filter((l) => validIds.has(l.i)),
    };
    setLayouts(filteredLayouts);
  }, []);

  const handleDragStop = useCallback((layout) => {
    setLayouts({ lg: layout });
    doSave(layout);
  }, [doSave]);

  const handleResizeStop = useCallback((layout) => {
    setLayouts({ lg: layout });
    doSave(layout);
  }, [doSave]);

  const handleRemoveWidget = useCallback((widgetId) => {
    const newWidgets = widgets.filter((w) => w.id !== widgetId);
    const newLayout  = (layouts.lg || []).filter((l) => l.i !== widgetId);
    setWidgets(newWidgets);
    setLayouts({ lg: newLayout });
    doSave(newLayout, newWidgets);
  }, [widgets, layouts, doSave]);

  const handleResetLayout = useCallback(() => {
    // Reset layout to full-width stacked widgets so the dashboard uses maximum horizontal space
    const colsLg = gridWidth >= 1200 ? 12 : gridWidth >= 996 ? 10 : gridWidth >= 768 ? 6 : 6;
    const colsMd = 10;
    const colsSm = 6;

    const newWidgets = [...DEFAULT_WIDGETS];
    const layoutLg = [];
    let y = 0;

    for (const w of newWidgets) {
      const reg = WIDGET_REGISTRY[w.type] || {};
      const h = reg.defaultH || 4;
      layoutLg.push({ i: w.id, x: 0, y, w: colsLg, h, minW: reg.minW, minH: reg.minH });
      y += h;
    }

    const layoutMd = layoutLg.map((it) => ({ ...it, w: Math.min(it.w, colsMd) }));
    const layoutSm = layoutLg.map((it) => ({ ...it, w: Math.min(it.w, colsSm) }));

    setWidgets(newWidgets);
    setLayouts({ lg: layoutLg, md: layoutMd, sm: layoutSm });
    doSave(layoutLg, newWidgets);
  }, [doSave, gridWidth]);

  const handleAddWidget = useCallback((type) => {
    const reg = WIDGET_REGISTRY[type];
    const id  = `w-${type}-${Date.now()}`;
    const currentLg = (layouts && layouts.lg) || [];
    const currentMd = (layouts && layouts.md) || [];
    const currentSm = (layouts && layouts.sm) || [];
    const maxY = currentLg.reduce((m, l) => Math.max(m, l.y + l.h), 0);
    // compute responsive cols and default to full-width for new widgets
    const colsLg = gridWidth >= 1200 ? 12 : gridWidth >= 996 ? 10 : gridWidth >= 768 ? 6 : 6;
    const colsMd = 10;
    const colsSm = 6;
    const newItemLg = { i: id, x: 0, y: maxY, w: colsLg, h: reg.defaultH, minW: reg.minW, minH: reg.minH };
    const newItemMd = { i: id, x: 0, y: currentMd.reduce((m, l) => Math.max(m, l.y + l.h), 0), w: Math.min(colsLg, colsMd), h: reg.defaultH, minW: reg.minW, minH: reg.minH };
    const newItemSm = { i: id, x: 0, y: currentSm.reduce((m, l) => Math.max(m, l.y + l.h), 0), w: Math.min(colsLg, colsSm), h: reg.defaultH, minW: reg.minW, minH: reg.minH };
    const newWidgets = [...widgets, { id, type }];
    const newLg = [...currentLg, newItemLg];
    const newMd = [...currentMd, newItemMd];
    const newSm = [...currentSm, newItemSm];
    setWidgets(newWidgets);
    setLayouts({ lg: newLg, md: newMd, sm: newSm });
    doSave(newLg, newWidgets);
    setAddDialogOpen(false);
  }, [widgets, layouts, uid, tenantId, gridWidth]);

  const renderWidget = (widget) => {
    const { id, type } = widget;
    const reg = WIDGET_REGISTRY[type] || {};

    const content = (() => {
      switch (type) {
        case "client_breakdown": return <ClientBreakdownWidget tenantId={effectiveTenantId} />;
        case "client_stats":     return <ClientStatsWidget tenantId={effectiveTenantId} />;
        case "upcoming_jobs":    return <UpcomingJobsWidget tenantId={effectiveTenantId} />;
        case "all_jobs":         return (
          <VuiBox p={2}>
            <VuiTypography sx={{ color: TC.secondary }}>Jobs Board removed — add an alternative widget.</VuiTypography>
          </VuiBox>
        );
        case "quick_actions":    return <QuickActionsWidget tenantId={effectiveTenantId} />;
        case "recent_clients":   return <RecentClientsWidget tenantId={effectiveTenantId} />;

        // New, tenant-aware widgets
        case "bank_balances":     return <BankBalancesWidget tenantId={effectiveTenantId} />;
        case "aged_receivables":  return <AgedReceivablesWidget tenantId={effectiveTenantId} />;
        case "vat_deadlines":     return <VATDeadlinesWidget tenantId={effectiveTenantId} />;
        case "cashflow_forecast": return <CashflowForecastWidget tenantId={effectiveTenantId} />;
        case "client_health":     return <ClientHealthWidget tenantId={effectiveTenantId} />;
        case "revenue_trend":     return <RevenueTrendWidget tenantId={effectiveTenantId} />;
        case "system_health":     return <SystemHealthWidget tenantId={effectiveTenantId} />;

        default:
          return (
            <VuiBox p={2}>
              <VuiTypography sx={{ color: TC.secondary }}>Unknown widget: {type}</VuiTypography>
            </VuiBox>
          );
      }
    })();

    return (
      <div key={id} style={{ position: "relative" }}>
        <WidgetFrame
          title={reg.label || type}
          icon={reg.icon}
          onRemove={() => handleRemoveWidget(id)}
          isEditing={isEditing}
        >
          {content}
        </WidgetFrame>
      </div>
    );
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <style>{GRID_PLACEHOLDER_CSS}</style>
      <VuiBox py={3}>
        {/* Toolbar: move widget controls above the greeting/header so widgets can use full width */}
        {/* floating toolbar moved into grid container */}

        {/* Header */}
        <VuiBox
          mb={3}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          sx={{ gap: 1 }}
        >
          <VuiBox>
            <VuiTypography variant="h4" fontWeight="bold"
              sx={{ color: TC.primary,  animation: "greetFadeUp 0.6s ease-out both" }}>
              {greeting}{displayName ? `, ${displayName}` : ""}{" "}
              <span style={{ display: "inline-block", transformOrigin: "70% 70%", animation: "waveHand 1.8s ease-in-out 0.4s 1 both" }}>👋</span>
            </VuiTypography>
            <VuiTypography sx={{ color: TC.secondary, fontSize: "0.85rem", mt: 0.3, animation: "greetSubFade 0.6s ease-out 0.15s both" }}>
              {new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </VuiTypography>
          </VuiBox>

          {/* Assistant (PoC) pane — lightweight helper under header */}
          <VuiBox>
            <AssistantPane />
          </VuiBox>
        </VuiBox>

        {/* Grid */}
        {layoutLoaded && widgets.length > 0 && gridWidth > 0 && (
          <div ref={gridContainerRef} style={{ position: 'relative' }}>
          <ResponsiveGridLayout
            className="layout"
            layouts={layouts}
            breakpoints={{ lg: 1200, md: 996, sm: 768 }}
            cols={{ lg: 12, md: 10, sm: 6 }}
            rowHeight={50}
            width={gridWidth}
            onLayoutChange={handleLayoutChange}
            onDragStop={handleDragStop}
            onResizeStop={handleResizeStop}
            isDraggable={isEditing}
            isResizable={isEditing}
            draggableHandle=".drag-handle"
            margin={[12, 12]}
            containerPadding={[0, 0]}
            preventCollision={false}
            compactType="vertical"
          >
            {widgets.map(renderWidget)}
          </ResponsiveGridLayout>
          </div>
        )}

        {/* Empty state */}
        {layoutLoaded && widgets.length === 0 && (
          <VuiBox
            p={6}
            textAlign="center"
            sx={{ border: "2px dashed rgba(255,255,255,0.1)", borderRadius: "16px" }}
          >
            <VuiTypography variant="h5" mb={1} sx={{ color: TC.primary }}>
              No widgets yet
            </VuiTypography>
            <VuiTypography variant="button" mb={2} display="block" sx={{ color: TC.primary }}>
              Click Customise → Add Widget to get started
            </VuiTypography>
            <VuiButton
              size="small"
              onClick={() => { setIsEditing(true); setAddDialogOpen(true); }}
              sx={{ color: theme.palette.text.primary, background: 'transparent', border: `1px solid ${theme.palette.text.primary}33` }}
            >
              + Add Widget
            </VuiButton>
          </VuiBox>
        )}
      </VuiBox>

      <AddWidgetDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onAdd={handleAddWidget}
        activeWidgetTypes={widgets.map((w) => w.type)}
      />

      {/* Widgets menu anchored to the floating Add button */}
      <Menu
        id="widgets-menu"
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        PaperProps={{ sx: (t) => ({
          background: t.palette.background.paper,
          color: t.palette.text.primary,
          minWidth: 240,
          '& .MuiMenuItem-root': { color: t.palette.text.primary },
          '& .MuiListItemIcon-root span': { color: t.palette.text.primary },
        }) }}
      >
        <MenuItem disabled sx={{ opacity: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', py: 1.5 }}>
          <ListItemText
            primary="Add widgets"
            secondary="Select to add to your dashboard"
            primaryTypographyProps={{ align: 'center', sx: { color: (t) => t.palette.text.primary, fontWeight: 700 } }}
            secondaryTypographyProps={{ align: 'center', sx: { color: (t) => t.palette.text.secondary, opacity: 0.95 } }}
          />
        </MenuItem>
        {Object.entries(WIDGET_REGISTRY).map(([type, reg]) => {
          const already = widgets.some(w => w.type === type);
          return (
            <MenuItem key={type} onClick={() => { setMenuAnchor(null); if (!already) handleAddWidget(type); }} disabled={already}>
              <ListItemIcon>
                <span style={{ fontSize: 18 }}>{reg.icon}</span>
              </ListItemIcon>
              <ListItemText primary={reg.label} primaryTypographyProps={{ sx: { color: (t) => t.palette.text.primary } }} />
            </MenuItem>
          );
        })}
      </Menu>

      {/* Floating utilities toolbar (fixed, bottom-right) */}
      <VuiBox sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1200, display: 'flex', flexDirection: 'column', gap: 1.25, pointerEvents: 'auto' }}>
        <Tooltip title="Add Widget" placement="left">
          <IconButton
            size="small"
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            aria-controls={menuAnchor ? 'widgets-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={menuAnchor ? 'true' : undefined}
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: theme.palette.common.white,
              bgcolor: 'primary.main',
              '&:hover': { bgcolor: 'primary.dark' },
              boxShadow: 3,
            }}
          >
            <Icon sx={{ fontSize: 20 }}>widgets</Icon>
          </IconButton>
        </Tooltip>

        <Tooltip title="Reset layout" placement="left">
          <IconButton
            size="small"
            onClick={() => handleResetLayout()}
            aria-label="Reset layout"
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: theme.palette.text.primary,
              background: 'rgba(0,0,0,0.06)',
              border: '1px solid rgba(0,0,0,0.08)',
            }}
          >
            <Icon sx={{ fontSize: 20 }}>refresh</Icon>
          </IconButton>
        </Tooltip>

        <Tooltip title={isEditing ? "Exit customise" : "Customise layout"} placement="left">
          <IconButton
            size="small"
            onClick={() => setIsEditing((e) => !e)}
            aria-label="Toggle customise mode"
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: theme.palette.text.primary,
              background: isEditing ? 'rgba(76,175,80,0.12)' : 'rgba(0,0,0,0.04)',
              border: isEditing ? '1px solid rgba(76,175,80,0.24)' : '1px solid rgba(0,0,0,0.06)',
              '&:hover': { background: isEditing ? 'rgba(76,175,80,0.16)' : 'rgba(0,0,0,0.08)' },
            }}
          >
            <Icon sx={{ fontSize: 20 }}>{isEditing ? 'check' : 'edit'}</Icon>
          </IconButton>
        </Tooltip>
      </VuiBox>

      <Footer />
    </DashboardLayout>
  );
}

